import {
  Avatar,
  AvatarGroup,
  Box,
  ClickAwayListener,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import type { PresencePeer } from "src/hooks/useArticlePresence";
import { initials } from "src/utils/presenceIdentity";

interface PresenceStackProps {
  others: PresencePeer[];
  me: { displayName: string; color: string };
  onRename: (name: string) => void;
  /** Use light text/icons for placement on a dark background (e.g. the navy navbar). */
  onDark?: boolean;
  /** sessionId of the current single-writer holder (ringed green). */
  holderSessionId?: string;
  /** Whether THIS session holds the lock (rings the "you" avatar). */
  iAmHolder?: boolean;
}

const HOLDER_RING = "0 0 0 2px #22C55E";

/**
 * Toolbar presence indicator: avatars of everyone else currently editing this
 * article, plus a "You · {name}" chip the user can click to rename themselves
 * (needed because the three internal users share one Cognito account, so the
 * login name can't tell them apart).
 */
const PresenceStack: React.FC<PresenceStackProps> = ({
  others,
  me,
  onRename,
  onDark,
  holderSessionId,
  iAmHolder,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(me.displayName);

  const nameColor = onDark ? "rgba(255,255,255,0.85)" : "#6B7280";
  const pencilColor = onDark ? "rgba(255,255,255,0.6)" : "#9CA3AF";

  const startEdit = () => {
    setDraft(me.displayName);
    setEditing(true);
  };

  const commit = () => {
    onRename(draft);
    setEditing(false);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {others.length > 0 && (
        <AvatarGroup
          max={4}
          sx={{
            "& .MuiAvatar-root": {
              width: 24,
              height: 24,
              fontSize: "0.65rem",
              border: "2px solid #fff",
            },
          }}
        >
          {others.map((peer) => {
            const isHolderPeer = !!holderSessionId && peer.sessionId === holderSessionId;
            const ring = isHolderPeer ? { boxShadow: HOLDER_RING } : {};
            const verb = isHolderPeer ? "is editing" : "is viewing";
            return peer.kind === "agent" ? (
              <Tooltip key={peer.sessionId} title={`${peer.displayName} (agent) ${verb}`} arrow>
                <Avatar sx={{ bgcolor: peer.color, fontSize: "0.85rem", ...ring }}>🤖</Avatar>
              </Tooltip>
            ) : (
              <Tooltip key={peer.sessionId} title={`${peer.displayName} ${verb}`} arrow>
                <Avatar sx={{ bgcolor: peer.color, ...ring }}>{initials(peer.displayName)}</Avatar>
              </Tooltip>
            );
          })}
        </AvatarGroup>
      )}

      {editing ? (
        <ClickAwayListener onClickAway={commit}>
          <TextField
            size="small"
            value={draft}
            autoFocus
            placeholder="Your name"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") setEditing(false);
            }}
            sx={{
              width: 120,
              "& .MuiInputBase-input": {
                py: 0.5,
                fontSize: "0.75rem",
                color: onDark ? "#fff" : undefined,
                caretColor: onDark ? "#fff" : undefined,
                "&::placeholder": onDark
                  ? { color: "rgba(255,255,255,0.6)", opacity: 1 }
                  : undefined,
              },
              ...(onDark && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.4)",
                },
                "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.6)",
                },
              }),
            }}
          />
        </ClickAwayListener>
      ) : (
        <Tooltip title={`This is you — “${me.displayName}”. Click to rename.`} arrow>
          <Box
            onClick={startEdit}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              maxWidth: 150,
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "0.6rem",
                bgcolor: me.color,
                ...(iAmHolder ? { boxShadow: HOLDER_RING } : {}),
              }}
            >
              {initials(me.displayName)}
            </Avatar>
            <Typography
              variant="caption"
              sx={{
                color: nameColor,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 92,
              }}
            >
              {me.displayName}
            </Typography>
            <Typography variant="caption" sx={{ color: pencilColor, lineHeight: 1 }}>
              ✎
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

export default PresenceStack;
